import {
  ExternalItemTypeDto,
  PriorityContactDto,
  ScheduledEventDto,
} from '@act/shared/data-transfer-objects';
import {
  PriorityContactDeletedEventPayload,
  PriorityContactEventTypes,
  PriorityContactUpsertedEventPayload,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { DateTime, Interval } from 'luxon';
import { PersistConfig, persistReducer } from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import storageSession from 'redux-persist/lib/storage/session';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { PaginationBounds } from 'toolkit/tables/types/table.types';
import { interactionRequests } from '../requests/interactions.requests';
import { priorityContactRequests } from '../requests/priority-contact.requests';
import { DocketState } from '../types/docketTypes';
import { createApiThunk } from '../utils/api-thunk';
import { IntervalSet, updateIntervalSet } from '../utils/intervals/intervals';
import { feedSlice } from './feedSlice';
import { newMembersLoaded } from './memberSlice';
import {
  loadScheduledEventsForGuide,
  scheduledEventsSlice,
} from './scheduledEventSlice';
import { selectTeamMember } from './selectedMemberSlice';
import { getTodos } from './todoSlice';
import type { WebsocketEventActions } from './websocketSlice';
import { resetRedux } from '../actions/shared-actions';
import { useAppSelector } from 'redux/store-hooks';
import { getExpiringAndNeedsActionUserNeedsForGuide } from './needs.slice';

// THUNKS/ACTIONS ==============================================================
export const getGuideInteractions = createApiThunk(
  interactionRequests.getGuideInteractions
);
export const getPriorityContacts = createApiThunk(
  priorityContactRequests.getPriorityListForGuide
);
type DocketPagePayload = {
  guideId: string;
  startEpochTime: number;
  endEpochTime: number;
};
export const loadDocketPageData = createAction<DocketPagePayload>(
  'docket/load-docket-page-data'
);

// INITIAL STATE ===============================================================
const priorityContactEntityAdapter = createEntityAdapter<
  PriorityContactDto,
  string
>({
  selectId: dto => dto.memberId,
});

const initialState: DocketState = {
  interactionsLoadingStatus: 'initial',
  priorityContactLoadingStatus: 'initial',
  priorityContacts: priorityContactEntityAdapter.getInitialState(),
  priorityContactsUpdatedAt: 0,
  loadedTimeRanges: [],
  cachedTabPreferences: { tabBounds: {} },
};

// SLICE =======================================================================
export const docketSlice = createSlice({
  name: 'docket',
  initialState,
  reducers: {
    updateLoadedTimeRanges: (
      state: DocketState,
      action: PayloadAction<{ loadedTimeRanges: IntervalSet }>
    ) => {
      state.loadedTimeRanges = action.payload.loadedTimeRanges;
    },
    // priority contact websockets ===============================
    priorityContactUpserted: (
      state: DocketState,
      action: PayloadAction<PriorityContactUpsertedEventPayload>
    ) => {
      priorityContactEntityAdapter.upsertMany(
        state.priorityContacts,
        action.payload.priorityContactView.priorityContacts
      );
    },
    priorityContactDeleted: (
      state: DocketState,
      action: PayloadAction<PriorityContactDeletedEventPayload>
    ) => {
      priorityContactEntityAdapter.removeOne(
        state.priorityContacts,
        action.payload.memberId
      );
    },
    // tab preferences ===========================================
    resetTabPreferences: (
      state: DocketState,
      action: PayloadAction<{ newGuideId?: string }>
    ) => {
      state.cachedTabPreferences = {
        lastGuideId: action.payload.newGuideId,
        tabBounds: {},
      };

      return state;
    },
    docketTabSelected: (
      state: DocketState,
      action: PayloadAction<{ tab: string }>
    ) => {
      state.cachedTabPreferences.tab = action.payload.tab;

      return state;
    },
    setDocketTabBounds: (
      state: DocketState,
      action: PayloadAction<{ tab: string; bounds: PaginationBounds }>
    ) => {
      state.cachedTabPreferences.tabBounds[action.payload.tab] =
        action.payload.bounds;

      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      // Get Guide Interactions ============================
      .addCase(getGuideInteractions.pending, state => {
        state.interactionsLoadingStatus = 'loading';
      })
      .addCase(getGuideInteractions.fulfilled, state => {
        state.interactionsLoadingStatus = 'success';
      })
      .addCase(getGuideInteractions.rejected, state => {
        state.interactionsLoadingStatus = 'failed';
      })
      // Get Priority Contact List ============================
      .addCase(getPriorityContacts.pending, state => {
        state.priorityContactLoadingStatus = 'loading';
      })
      .addCase(getPriorityContacts.fulfilled, (state, action) => {
        state.priorityContactLoadingStatus = 'success';
        state.priorityContactsUpdatedAt = action.payload.updatedAt;
        priorityContactEntityAdapter.upsertMany(
          state.priorityContacts,
          action.payload.priorityContacts
        );
      })
      .addCase(getPriorityContacts.rejected, state => {
        state.priorityContactLoadingStatus = 'failed';
      })
      // Feed Websocket actions =============================
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        // only need to deal with "interactions" i.e. note, visit, call, or message
        const todayInterval = Interval.fromDateTimes(
          DateTime.now().startOf('day'),
          DateTime.now().endOf('day')
        );
        if (
          action.payload.feedItemModelView.feedItems.find(fi => {
            const interactionTypes: ExternalItemTypeDto[] = [
              'visit',
              'call',
              'note',
              'message',
            ];
            const isInteractionType = interactionTypes.includes(
              fi.externalItemType
            );
            const happenedToday = todayInterval.contains(
              DateTime.fromMillis(fi.timestamp)
            );
            return isInteractionType && happenedToday;
          })
        ) {
          priorityContactEntityAdapter.removeOne(
            state.priorityContacts,
            action.payload.userId
          );
        }
      })
      // Scheduled event websocket actions =============================
      .addCase(
        scheduledEventsSlice.actions.scheduledEventCreated,
        (state, action) => {
          reduceScheduledEvent(state, action.payload.scheduledEvent);
        }
      )
      .addCase(
        scheduledEventsSlice.actions.scheduledEventUpdated,
        (state, action) => {
          reduceScheduledEvent(state, action.payload.scheduledEvent);
        }
      )
      .addCase(selectTeamMember, state => {
        state.loadedTimeRanges = [];
      });
  },
});
export const docketActions = docketSlice.actions;

// REDUCER =====================================================================
const persistConfig: PersistConfig<DocketState> = {
  key: 'docket',
  storage: storageSession,
  whitelist: ['cachedTabPreferences'],
  stateReconciler: autoMergeLevel1,
};
export const docketReducer = persistReducer(persistConfig, docketSlice.reducer);

function reduceScheduledEvent(
  state: DocketState,
  scheduledEvent: ScheduledEventDto
) {
  const thisMonthInterval = Interval.fromDateTimes(
    DateTime.now().startOf('month'),
    DateTime.now().endOf('month')
  );
  if (thisMonthInterval.contains(DateTime.fromISO(scheduledEvent.date))) {
    priorityContactEntityAdapter.removeOne(
      state.priorityContacts,
      scheduledEvent.userId
    );
  }
}

// SELECTORS ===================================================================
const selectDocketState = state =>
  (state as { docket: typeof initialState })?.docket;
const selectPriorityContactState = state =>
  selectDocketState(state)?.priorityContacts;

const { selectAll: selectPriorityContacts } =
  priorityContactEntityAdapter.getSelectors(selectPriorityContactState);

export interface GuideSelectorParams {
  guideId: string;
  [key: string]: unknown;
}
export const selectPriorityContactsByGuide: (
  state,
  paramObj: GuideSelectorParams
) => PriorityContactDto[] = createSelector(
  [
    selectPriorityContacts,
    (state, paramObj: GuideSelectorParams) => paramObj.guideId,
  ],
  (priorityContacts, guideId) => {
    const filtered = priorityContacts.filter(pc => pc.guideId === guideId);
    filtered.sort((a, b) => a.sortOrder - b.sortOrder);
    return filtered;
  }
);

export const selectInteractionsLoadingState = state =>
  selectDocketState(state).interactionsLoadingStatus;

export const selectPriorityContactLoadingState = state =>
  selectDocketState(state).priorityContactLoadingStatus;

export const selectTabPreferences = state =>
  selectDocketState(state).cachedTabPreferences;

const selectPriorityContactLastUpdatedAt = state =>
  selectDocketState(state).priorityContactsUpdatedAt;

// EXPORT HELPER ===============================================================
export const docketSliceSelectors = {
  selectPriorityContactsByGuide,
  selectInteractionsLoadingState,
  selectPriorityContactLoadingState,
  selectTabPreferences,
  useSelectPriorityContactLastUpdatedAt: () =>
    useAppSelector(selectPriorityContactLastUpdatedAt),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadDocketPageData,
    effect: async ({ action, dispatch, state }) => {
      const { startEpochTime, endEpochTime, guideId } = action.payload;

      // If docket was loaded for a non-guide, don't request data.
      if (!guideId || guideId === 'none') {
        return;
      }

      const docketState = state.docket;
      const newTimeRange = {
        start: action.payload.startEpochTime,
        end: action.payload.endEpochTime,
      };

      const { intervals: newLoadedTimeRange, wasChanged: timeRangeChanged } =
        updateIntervalSet(docketState.loadedTimeRanges, newTimeRange);

      if (timeRangeChanged) {
        // Keep track of what time ranges have already been loaded
        dispatch(
          docketSlice.actions.updateLoadedTimeRanges({
            loadedTimeRanges: newLoadedTimeRange,
          })
        );

        const now = new Date().getTime();

        // LOAD INTERACTIONS
        // Get Interactions for that time range if any part of that time range is in the past
        if (startEpochTime < now) {
          dispatch(
            getGuideInteractions({
              params: {
                guideId: action.payload.guideId,
                startEpochTime,
                endEpochTime,
              },
            })
          );
        }

        // LOAD TODOS, PRIORITY CONTACT, AND NEEDS
        // Get Todo items if time range includes current time because TODOs are only actionable now
        if (startEpochTime < now && now < endEpochTime) {
          dispatch(
            getTodos({
              params: { guideId, includeMemberDetails: true },
            })
          );
          dispatch(
            getPriorityContacts({
              params: { guideId, includeMemberDetails: true },
            })
          );
          dispatch(
            getExpiringAndNeedsActionUserNeedsForGuide({
              params: { guideId },
            })
          );
        }

        // LOAD SCHEDULED EVENTS
        // Get Scheduled Events if we aren't looking in the past.
        if (endEpochTime > now) {
          let scheduledEventStartEpochTime = endEpochTime;

          // If now is in the current time range then we should get all past scheduled events so we can show late scheduled events
          if (now < endEpochTime) {
            scheduledEventStartEpochTime = 0;
          }

          dispatch(
            loadScheduledEventsForGuide({
              userId: guideId,
              startEpochTime: scheduledEventStartEpochTime,
              endEpochTime,
            })
          );
        }
      }
    },
  });
  addEffect({
    action: getGuideInteractions.fulfilled,
    effect: async ({ action, dispatch }) => {
      dispatch(
        newMembersLoaded({
          members: action.payload.members,
        })
      );
    },
  });
  addEffect({
    action: getPriorityContacts.fulfilled,
    effect: async ({ dispatch, action }) => {
      dispatch(
        newMembersLoaded({
          members: action.payload.members,
        })
      );
    },
  });
  addEffect({
    action: getGuideInteractions.fulfilled,
    effect: async ({ dispatch, action }) => {
      dispatch(newMembersLoaded({ members: action.payload.members }));
    },
  });
  addEffect({
    action: selectTeamMember,
    effect: async ({ dispatch, action, state }) => {
      const lastGuideId = selectTabPreferences(state)?.lastGuideId;
      if (lastGuideId !== action.payload) {
        void dispatch(
          docketSlice.actions.resetTabPreferences({
            newGuideId: action.payload,
          })
        );
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const docketSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: PriorityContactEventTypes.PRIORITY_CONTACT_UPSERTED,
    action: docketSlice.actions.priorityContactUpserted,
    channel: 'baseSocket',
  },
  {
    type: PriorityContactEventTypes.PRIORITY_CONTACT_DELETED,
    action: docketSlice.actions.priorityContactDeleted,
    channel: 'baseSocket',
  },
];
